/**
 * Toutes les écritures : la liste paginée, filtrée par exercice, journal,
 * période, statut, compte, et une recherche.
 *
 * ## L'adresse porte les filtres
 *
 * `?journal=VEN&exercice=2026` : c'est par là que la page des journaux, le
 * tableau de bord et la fiche d'une écriture ramènent ici. Une liste trouvée
 * se partage et se recharge ; revenir d'une fiche retrouve la même liste.
 *
 * ## Ce que la liste montre tel quel
 *
 * Un brouillon n'a pas de numéro : il se lit « Brouillon ». S'il est
 * déséquilibré, l'écart se lit sur sa ligne. Une écriture extournée et son
 * extourne se désignent l'une l'autre. Aucun geste d'ici.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, ArrowRight, ListOrdered, Loader2, RefreshCw, Search,
} from 'lucide-react';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import { ficheEcriture } from '../../../lib/navigation/chemins';
import {
  chargerEcritures, lireLesCodesDeJournaux, lireLesExercices, TAILLE_DE_PAGE,
  type ExerciceTenu,
} from '../../../lib/compta/ecritures/gateway.supabase';
import {
  lireFiltres, lireRechercheEcritures, MOIS, NATURES, referenceAffichee, STATUTS_ECRITURE, TRIS,
  versParametres, type EcritureListee, type FiltresEcritures, type StatutEcriture, type Tri,
} from '../../../lib/compta/ecritures/types';
import { dateCourte, euros } from '../components/money';

export default function EcrituresPage() {
  const [parametres, setParametres] = useSearchParams();
  /* Les filtres naissent de l'adresse, une fois ; ensuite, l'adresse suit les filtres. */
  const [filtres, setFiltres] = useState<FiltresEcritures>(() => lireFiltres(parametres));
  const [saisie, setSaisie] = useState(filtres.recherche);
  const [saisieCompte, setSaisieCompte] = useState(filtres.compte ?? '');
  const [page, setPage] = useState(0);

  const [lignes, setLignes] = useState<EcritureListee[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [exercices, setExercices] = useState<ExerciceTenu[]>([]);
  const [journaux, setJournaux] = useState<{ code: string; libelle: string }[]>([]);

  useEffect(() => {
    let vivant = true;
    Promise.all([lireLesExercices(), lireLesCodesDeJournaux()])
      .then(([ex, j]) => { if (vivant) { setExercices(ex); setJournaux(j); } })
      .catch(() => { /* les listes des filtres manquent ; la liste, elle, dira son erreur */ });
    return () => { vivant = false; };
  }, []);

  /*
    Une adresse qui change sans que la page soit remontée — l'entrée « Toutes
    les écritures » du menu, cliquée depuis une liste filtrée — porte d'autres
    filtres : la liste les suit. Les changements faits ici écrivent déjà
    l'adresse qu'ils lisent : l'égalité les laisse passer sans boucle.
  */
  useEffect(() => {
    const lus = lireFiltres(parametres);
    if (versParametres(lus).toString() === versParametres(filtres).toString()) return;
    setFiltres(lus);
    setSaisie(lus.recherche);
    setSaisieCompte(lus.compte ?? '');
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seule l'adresse déclenche ; `filtres` n'est que comparé
  }, [parametres]);

  const comprise = useMemo(() => lireRechercheEcritures(saisie), [saisie]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await chargerEcritures(filtres, page * TAILLE_DE_PAGE);
      setLignes(r.lignes);
      setTotal(r.total);
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture des écritures impossible'));
    } finally {
      setChargement(false);
    }
  }, [filtres, page]);

  useEffect(() => { void charger(); }, [charger]);

  /* Changer un critère ramène à la première page, et l'adresse suit. */
  const changer = (modif: Partial<FiltresEcritures>) => {
    const suivants = { ...filtres, ...modif };
    setFiltres(suivants);
    setPage(0);
    setParametres(versParametres(suivants), { replace: true });
  };

  const pages = total === null ? 1 : Math.max(1, Math.ceil(total / TAILLE_DE_PAGE));
  const filtresPoses = versParametres({ ...filtres, tri: 'recentes' }).toString() !== '';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ListOrdered className="h-6 w-6 text-primary-600" /> Écritures
          </h1>
          <p className="max-w-3xl text-gray-600">
            Chaque écriture, de la pièce aux comptes : journal, numéro, débit et crédit, tiers. Lecture seule.
          </p>
        </div>
        <button type="button" onClick={charger} disabled={chargement} className="btn-primary flex items-center">
          <RefreshCw className={clsx('mr-2 h-4 w-4', chargement && 'animate-spin')} />
          Actualiser
        </button>
      </div>

      {/* --- Chercher et filtrer ------------------------------------------ */}
      <div className="space-y-2">
        <form
          onSubmit={(e) => { e.preventDefault(); changer({ recherche: saisie.trim() }); }}
          className="flex flex-col gap-3 md:flex-row md:items-center"
        >
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Référence (VEN-2026-2709), facture, famille, compte, libellé…"
              className="form-input w-full py-2 pl-10 pr-4"
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          </div>
          <button type="submit" className="btn-outline">Chercher</button>
        </form>
        {comprise.explication && <p className="text-sm text-gray-500">{comprise.explication}</p>}

        <div className="flex flex-wrap gap-3">
          <select className="form-input w-auto py-2" aria-label="Exercice" value={filtres.exercice ?? ''}
                  onChange={(e) => changer({ exercice: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Tous les exercices</option>
            {exercices.map((e) => <option key={e.annee} value={e.annee}>{e.annee}</option>)}
          </select>
          <select className="form-input w-auto py-2" aria-label="Journal" value={filtres.journal ?? ''}
                  onChange={(e) => changer({ journal: e.target.value || null })}>
            <option value="">Tous les journaux</option>
            {journaux.map((j) => <option key={j.code} value={j.code}>{j.code} — {j.libelle}</option>)}
          </select>
          <select className="form-input w-auto py-2" aria-label="Période" value={filtres.periode ?? ''}
                  onChange={(e) => changer({ periode: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Toutes les périodes</option>
            {MOIS.map((m, i) => <option key={m} value={i + 1}>{String(i + 1).padStart(2, '0')} — {m}</option>)}
          </select>
          <select className="form-input w-auto py-2" aria-label="Statut" value={filtres.statut ?? ''}
                  onChange={(e) => changer({ statut: (e.target.value || null) as StatutEcriture | null })}>
            <option value="">Validées et brouillons</option>
            <option value="validee">Validées</option>
            <option value="brouillon">Brouillons</option>
          </select>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Compte (400000)"
            aria-label="Compte"
            className="form-input w-40 py-2"
            value={saisieCompte}
            onChange={(e) => setSaisieCompte(e.target.value)}
            onBlur={() => {
              const v = saisieCompte.trim();
              const compte = /^\d{1,10}$/.test(v) ? v : null;
              if (compte !== filtres.compte) changer({ compte });
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
          <select className="form-input w-auto py-2" aria-label="Tri" value={filtres.tri}
                  onChange={(e) => changer({ tri: e.target.value as Tri })}>
            {(Object.keys(TRIS) as Tri[]).map((t) => <option key={t} value={t}>{TRIS[t].label}</option>)}
          </select>
          {filtresPoses && (
            <button type="button" className="text-sm text-primary-700 hover:underline"
                    onClick={() => { setSaisie(''); setSaisieCompte(''); changer({ exercice: null, journal: null, periode: null, statut: null, compte: null, recherche: '' }); }}>
              Retirer les filtres
            </button>
          )}
        </div>
      </div>

      {!chargement && !erreur && (
        <p className="text-sm text-gray-600">
          {total === 0 ? 'Aucune écriture ne correspond.' : (
            <>
              <strong className="tabular-nums">{total?.toLocaleString('fr-BE')}</strong> écriture
              {(total ?? 0) > 1 ? 's' : ''} correspond{(total ?? 0) > 1 ? 'ent' : ''}. {lignes.length} affichée
              {lignes.length > 1 ? 's' : ''} ici.
            </>
          )}
        </p>
      )}

      {chargement ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : erreur ? (
        <div className="rounded-lg bg-red-50 p-4">
          <p className="flex items-start gap-2 text-red-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> {erreur}
          </p>
        </div>
      ) : lignes.length === 0 ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-md">
          <p className="text-gray-600">Aucune écriture ne correspond à ces critères.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white shadow-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Référence</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Libellé et tiers</th>
                  <th className="px-4 py-3">Comptes</th>
                  <th className="px-4 py-3 text-right">Montant</th>
                  <th className="px-4 py-3">État</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lignes.map((e) => {
                  const ecart = e.totalDebitCents - e.totalCreditCents;
                  return (
                    <tr key={e.id} className="align-top">
                      <td className="whitespace-nowrap px-4 py-3">
                        <Link to={ficheEcriture(e.id)} state={{ retour: `?${parametres.toString()}` }}
                              className={clsx('font-medium hover:underline',
                                e.statut === 'brouillon' ? 'text-amber-800' : 'text-primary-700')}>
                          {referenceAffichee(e)}
                        </Link>
                        {e.statut === 'brouillon' && <span className="ml-1 text-xs text-gray-500">{e.journal}</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                        {dateCourte(e.date)}
                        {e.dateOrigine && (
                          <span className="block text-xs text-gray-500" title="Période économique d’origine (décision 9)">
                            origine {dateCourte(e.dateOrigine)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-900">{e.libelle}</span>
                        {e.tiersNoms && <span className="block text-xs text-gray-500">{e.tiersNoms}</span>}
                      </td>
                      <td className="px-4 py-3 text-xs tabular-nums text-gray-600">{e.comptes.join(' · ')}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                        {euros(e.totalDebitCents)}
                        {ecart !== 0 && (
                          <span className="block text-xs font-medium text-red-700">
                            crédit {euros(e.totalCreditCents)} — écart {euros(Math.abs(ecart))}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={clsx('rounded-full px-2 py-0.5 text-xs', STATUTS_ECRITURE[e.statut].classe)}>
                          {STATUTS_ECRITURE[e.statut].label}
                        </span>
                        {e.nature !== 'ordinaire' && (
                          <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                            {NATURES[e.nature] ?? e.nature}
                          </span>
                        )}
                        {e.extourneeParId && (
                          <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">extournée</span>
                        )}
                        {e.correctionAnterieure && (
                          <span className="ml-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-800">
                            correction {e.exerciceOrigine}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* --- Pagination ------------------------------------------------ */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium tabular-nums">{page + 1}</span> sur{' '}
              <span className="font-medium tabular-nums">{pages}</span>
            </p>
            <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                      className={clsx('relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium',
                        page === 0 ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-gray-50')}
                      aria-label="Page précédente">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page + 1 >= pages}
                      className={clsx('relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium',
                        page + 1 >= pages ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-gray-50')}
                      aria-label="Page suivante">
                <ArrowRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
